import React from "react";
import KuikaCodeText from "../../../../ui-kit/typography/CodeText/CodeText";
import KuikaH1 from "../../../../ui-kit/typography/H1/H1";
import KuikaH2 from "../../../../ui-kit/typography/H2/H2";
import KuikaH3 from "../../../../ui-kit/typography/H3/H3";
import KuikaH4 from "../../../../ui-kit/typography/H4/H4";
import KuikaH5 from "../../../../ui-kit/typography/H5/H5";
import KuikaH6 from "../../../../ui-kit/typography/H6/H6";
import KuikaH7 from "../../../../ui-kit/typography/H7/H7";
import KuikaH8 from "../../../../ui-kit/typography/H8/H8";
import KuikaSmallBoldText from "../../../../ui-kit/typography/SmallBoldText/SmallBoldText";
import KuikaSmallMediumText from "../../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../../../ui-kit/typography/Text/Text";
import KuikaTextBold from "../../../../ui-kit/typography/TextBold/TextBold";
import KuikaTextMedium from "../../../../ui-kit/typography/TextMedium/TextMedium";
import KuikaTitle from "../../../../ui-kit/typography/Title/Title";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";

interface ITypoSpec {
  name: string;
  caption: string;
  render: () => JSX.Element;
}

const DISPLAY_SAMPLES: ITypoSpec[] = [
  { name: "H1", caption: "display · large", render: () => <KuikaH1>Build with calm primitives</KuikaH1> },
  { name: "H2", caption: "section header", render: () => <KuikaH2>Build with calm primitives</KuikaH2> },
  { name: "H3", caption: "section header", render: () => <KuikaH3>Build with calm primitives</KuikaH3> },
  { name: "H4", caption: "card title", render: () => <KuikaH4>Build with calm primitives</KuikaH4> },
  { name: "H5", caption: "card title", render: () => <KuikaH5>Build with calm primitives</KuikaH5> },
  { name: "H6", caption: "label / eyebrow", render: () => <KuikaH6>Build with calm primitives</KuikaH6> },
  { name: "H7", caption: "label / eyebrow", render: () => <KuikaH7>Build with calm primitives</KuikaH7> },
  { name: "H8", caption: "micro label", render: () => <KuikaH8>Build with calm primitives</KuikaH8> },
  { name: "Title", caption: "marketing display", render: () => <KuikaTitle>Build with calm primitives</KuikaTitle> }
];

const BODY_SAMPLES: ITypoSpec[] = [
  {
    name: "Text",
    caption: "default body",
    render: () => <KuikaText>The quick brown fox jumps over the lazy dog.</KuikaText>
  },
  {
    name: "TextMedium",
    caption: "medium weight",
    render: () => <KuikaTextMedium>The quick brown fox jumps over the lazy dog.</KuikaTextMedium>
  },
  {
    name: "TextBold",
    caption: "strong emphasis",
    render: () => <KuikaTextBold>The quick brown fox jumps over the lazy dog.</KuikaTextBold>
  },
  { name: "CodeText", caption: "monospaced inline", render: () => <KuikaCodeText>npm run build</KuikaCodeText> }
];

const SMALL_SAMPLES: ITypoSpec[] = [
  {
    name: "SmallText",
    caption: "secondary",
    render: () => <KuikaSmallText>Generated 12 minutes ago — last edit by furkan.</KuikaSmallText>
  },
  {
    name: "SmallMediumText",
    caption: "medium small",
    render: () => <KuikaSmallMediumText>Generated 12 minutes ago — last edit by furkan.</KuikaSmallMediumText>
  },
  {
    name: "SmallBoldText",
    caption: "bold small",
    render: () => <KuikaSmallBoldText>Generated 12 minutes ago — last edit by furkan.</KuikaSmallBoldText>
  }
];

interface ISampleRowProps {
  sample: ITypoSpec;
  nameWidth?: number;
}

const SampleRow: React.FC<ISampleRowProps> = ({ sample, nameWidth = 110 }: ISampleRowProps) => (
  <div className="lab-row" style={{ width: "100%" }}>
    <div style={{ width: nameWidth }} className="lab-caption">
      {sample.name}
      <div style={{ color: "var(--lab-ink-faint)", marginTop: 2 }}>{sample.caption}</div>
    </div>
    <div>{sample.render()}</div>
  </div>
);

export const TypographyStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Typography"
      componentName="KuikaH1 · KuikaText · KuikaSmallText"
      sourcePath="ui-kit/typography"
      description="Display, heading and body scales. Composed from individual components rather than a single polymorphic primitive."
    >
      <SpecRow label="Display & headings" caption="H1 → H8, plus Title">
        <div className="lab-stack" style={{ gap: 18, width: "100%" }}>
          {DISPLAY_SAMPLES.map((s: ITypoSpec) => (
            <SampleRow key={s.name} sample={s} />
          ))}
        </div>
      </SpecRow>
      <SpecRow label="Body" caption="Text · TextMedium · TextBold · CodeText">
        <div className="lab-stack" style={{ gap: 14, width: "100%" }}>
          {BODY_SAMPLES.map((s: ITypoSpec) => (
            <SampleRow key={s.name} sample={s} />
          ))}
        </div>
      </SpecRow>
      <SpecRow label="Small / caption" caption="SmallText family">
        <div className="lab-stack" style={{ gap: 14, width: "100%" }}>
          {SMALL_SAMPLES.map((s: ITypoSpec) => (
            <SampleRow key={s.name} sample={s} nameWidth={130} />
          ))}
        </div>
      </SpecRow>
    </ShowcaseFrame>
  );
};
